import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAppWorkspace } from "@/contexts/AppWorkspaceContext";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

/**
 * WorkspaceSettingsSection
 *
 * General workspace settings (name, app config)
 * Includes delete workspace for owners
 */
export function WorkspaceSettingsSection() {
  const { activeWorkspace, userRole, updateWorkspace, deleteWorkspace } =
    useAppWorkspace();

  const [name, setName] = useState(activeWorkspace?.name || "");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  if (!activeWorkspace) {
    return <div>No workspace selected</div>;
  }

  const canEdit = userRole === "owner" || userRole === "admin";
  const canDelete = userRole === "owner";

  const handleSave = async () => {
    if (!canEdit) return;

    setIsSaving(true);
    try {
      await updateWorkspace(activeWorkspace._id, { name: name.trim() });
      toast.success("Workspace updated");
    } catch (error) {
      console.error("Failed to update workspace:", error);
      toast.error("Failed to update workspace");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete) return;

    setIsDeleting(true);
    try {
      await deleteWorkspace(activeWorkspace._id);
      toast.success("Workspace deleted");
    } catch (error) {
      console.error("Failed to delete workspace:", error);
      toast.error("Failed to delete workspace");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-4">General</h1>

        <div className="space-y-4 max-w-xl">
          <div className="space-y-2">
            <Label htmlFor="workspace-name">Workspace Name</Label>
            <Input
              id="workspace-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!canEdit}
              placeholder="My Workspace"
            />
            {canEdit && (
              <Button
                onClick={handleSave}
                disabled={isSaving || name.trim() === activeWorkspace.name}
                className="mt-2"
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            )}
          </div>

          <div className="space-y-2">
            <Label>Workspace ID</Label>
            <Input value={activeWorkspace._id} disabled />
            <p className="text-xs text-muted-foreground">
              Use this ID for API integrations
            </p>
          </div>

          <div className="space-y-2">
            <Label>Billing Status</Label>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-950 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 capitalize">
                {activeWorkspace.billingStatus}
              </span>
            </div>
          </div>
        </div>
      </div>

      {canDelete && (
        <div className="border-t pt-6">
          <h4 className="text-base font-semibold text-destructive mb-2">
            Danger Zone
          </h4>
          <p className="text-sm text-muted-foreground mb-4">
            Once you delete a workspace, there is no going back. Please be
            certain.
          </p>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" disabled={isDeleting}>
                Delete Workspace
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete the
                  workspace
                  <strong> {activeWorkspace.name}</strong> and remove all
                  members.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground"
                >
                  {isDeleting ? "Deleting..." : "Delete Workspace"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}
